/**
 * CardOrgSharing — per-card "shared with organizations" chips (task #80).
 *
 * Rendered under each saved card on the wallet page. Cards are always the
 * user's own; a chip toggles whether THAT organization may see the card
 * (and, for members its owner's org has granted the use permission, pay with
 * it). Organizations the user doesn't belong to never appear.
 */

import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import { doc, getDoc, getFirestore } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { fetchWorkspaceOptionsCached } from '../../services/workspaceOptionsCache';
import { setPaymentMethodOrgSharing } from '../../services/paymentMethodSharing';

const Row = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.4rem;
  margin-top: 0.45rem;
`;

const RowLabel = styled.span`
  font-size: 0.72rem;
  color: #857f73;
`;

const OrgChip = styled.button<{ $on: boolean }>`
  border: 1px solid ${(p) => (p.$on ? 'var(--brand-gold)' : 'var(--brand-line)')};
  background: ${(p) => (p.$on ? 'rgba(198, 167, 94, 0.12)' : 'transparent')};
  color: ${(p) => (p.$on ? 'var(--brand-gold-text)' : '#857f73')};
  border-radius: 999px;
  font-size: 0.72rem;
  padding: 0.18rem 0.6rem;
  cursor: pointer;
  transition: border-color 0.15s ease, background 0.15s ease;

  &:hover {
    border-color: var(--brand-gold);
  }

  &:disabled {
    opacity: 0.6;
    cursor: default;
  }
`;

interface OrgOption {
  accountId: string;
  label: string;
}

export const CardOrgSharing: React.FC<{ paymentMethodId: string }> = ({ paymentMethodId }) => {
  const [orgs, setOrgs] = useState<OrgOption[] | null>(null);
  const [shared, setShared] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let canceled = false;
    (async () => {
      const uid = getAuth().currentUser?.uid;
      if (!uid) return;
      try {
        const [options, walletSnap] = await Promise.all([
          fetchWorkspaceOptionsCached(uid),
          getDoc(doc(getFirestore(), `users/${uid}/user_wallet/user_wallet`)),
        ]);
        if (canceled) return;
        const orgOptions = (options ?? [])
          .filter((o) => o.accountId !== uid)
          .map((o) => ({ accountId: o.accountId, label: o.displayName || o.accountId }));
        setOrgs(orgOptions);
        const map = (walletSnap.data()?.pm_org_sharing ?? {}) as Record<string, string[]>;
        setShared(Array.isArray(map[paymentMethodId]) ? map[paymentMethodId] : []);
      } catch {
        if (!canceled) setOrgs([]);
      }
    })();
    return () => {
      canceled = true;
    };
  }, [paymentMethodId]);

  const [toggleError, setToggleError] = useState<string | null>(null);
  const toggle = useCallback(
    async (accountId: string) => {
      if (busy) return;
      const next = shared.includes(accountId)
        ? shared.filter((id) => id !== accountId)
        : [...shared, accountId];
      const prev = shared;
      setShared(next);
      setToggleError(null);
      setBusy(true);
      const res = await setPaymentMethodOrgSharing(paymentMethodId, next);
      if (!res.ok) {
        // 2026-08-18 audit: the silent revert read as a broken chip —
        // say what happened instead.
        setShared(prev);
        setToggleError('sharing change didn’t save — try again.');
      }
      setBusy(false);
    },
    [busy, shared, paymentMethodId]
  );

  // Options still loading → a quiet placeholder (not a blank that pops in).
  if (orgs === null) {
    return (
      <Row data-card-org-sharing>
        <RowLabel>checking your organizations…</RowLabel>
      </Row>
    );
  }
  // No organizations to share with → nothing to render.
  if (orgs.length === 0) return null;

  return (
    <Row data-card-org-sharing>
      <RowLabel>shared with:</RowLabel>
      {orgs.map((org) => {
        const on = shared.includes(org.accountId);
        return (
          <OrgChip
            key={org.accountId}
            type="button"
            $on={on}
            disabled={busy}
            onClick={() => void toggle(org.accountId)}
            title={
              on
                ? `stop sharing this card with ${org.label}`
                : `share this card with ${org.label} — members its owner has given the card permissions can then see and use it there`
            }
          >
            {org.label}
            {on ? ' ✓' : ''}
          </OrgChip>
        );
      })}
      {toggleError && (
        <RowLabel role="alert" style={{ color: '#9B2C2C' }}>{toggleError}</RowLabel>
      )}
    </Row>
  );
};
